"use client";
import "@/styles/home/approach.css";
import { useRef, useState } from "react";

import { m } from "framer-motion";
import { twMerge } from "tailwind-merge";

import { ApproachData } from "@/constants/Data";

import Button from "@/components/common/Button";

import { useIsDark } from "@/context/ThemeContext";

export default function Approach() {
  const isdark = useIsDark();

  const refs = useRef([]);

  const [triggerFirstCubes, setTriggerFirstCubes] = useState(false);
  const [triggerSecondCubes, setTriggerSecondCubes] = useState(false);
  const [visibleStep, setVisibleStep] = useState(-1);
  const [start, setStart] = useState(-1);

  const fadeInUpHeaders = {
    initial: {
      filter: "saturate(0) contrast(4) brightness(0.1) blur(5px)",
      opacity: 0,
      scale: 0.95,
    },
    whileInView: {
      filter: "none",
      opacity: 1,
      scale: 1,
    },
    viewport: { once: true, margin: "-5% 0px -5% 0px", amount: 0.8 },
    transition: {
      duration: 0.9,
      ease: [0.25, 0.1, 0.25, 1],
    },
  };

  return (
    <div
      className="flex flex-col sm:flex-row w-full gap-[5vw] max-w-[2000px] relative "
      // ref={scope}
    >
      {/* header for the tablet and large screen sizes */}
      <div className="hidden sm:block ">
        <m.div
          {...fadeInUpHeaders}
          className="will-change-transform will-change-filter"
        >
          <p className="font-alexandria font-extrabold text-[28px] md:text-[32px] lg:text-[40px] xl:text-[52px] uppercase 3xl:text-[58px] text-foreground text-left mb-3 tablet:mb-7 xl:mb-9">
            OUR APPROACH:
            <br />
            FROM
            <br />
            BUSINESS
            <br />
            CHALLENGE TO
            <br />
            ENTERPRISE INTELLIGENCE
          </p>
          <Button
            label="Let's Talk"
            href="/contact#contact-form"
            className={isdark ? "" : "bg-[#DF27DF]"}
          />
        </m.div>
      </div>

      {/* for mobile screens - without breakpoints - full width */}
      <div className="sm:hidden">
        <m.div
          {...fadeInUpHeaders}
          className="will-change-transform will-change-filter flex flex-col items-center"
        >
          <p className="font-alexandria font-extrabold text-[22px] text-center mb-3">
            OUR APPROACH: FROM BUSINESS CHALLENGE TO ENTERPRISE INTELLIGENCE
          </p>
          <Button label="Let's Talk" className={isdark ? "" : "bg-[#FF1CF7]"} />
        </m.div>
      </div>

      {/* right side data line */}
      <div className="flex flex-col pt-6">
        {ApproachData.map((item, i, arr) => (
          <m.div
            key={i}
            className="flex gap-2 laptop:gap-4 h-30 lg:h-[120px] xl:h-[174px]"
            ref={(el) => (refs.current[i] = el)}
            whileInView={() => {
              setVisibleStep(i);
              if (i === 1 && !triggerFirstCubes) setTriggerFirstCubes(true);
              if (i === 5 && !triggerSecondCubes) setTriggerSecondCubes(true);
            }}
            viewport={{ once: true, amount: 0.5 }}
          >
            <div className="flex flex-col items-center">
              {/* number circle */}
              <m.div
                initial={{ opacity: 0, y: 100 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="font-albertSans text-[14px] lg:text-[17px] xl:text-[20px] flex justify-center items-center text-center border-2 border-[#FF1CF7] font-semibold rounded-full w-[30px] h-[30px] lg:w-[40px] lg:h-[40px] xl:w-[56px] xl:h-[55px] 4xl:w-[70px] 4xl:h-[71px]"
              >
                0{i + 1}
              </m.div>

              {/* line - starts when next number appears */}
              {i !== arr.length - 1 && (
                <m.div
                  initial={{ scaleY: 0 }}
                  animate={visibleStep > i ? { scaleY: 1 } : { scaleY: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
                  className="w-[1px] bg-[#FF1CF7] flex-1 origin-top"
                />
              )}
            </div>

            {/* text */}
            <m.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              onViewportEnter={() => setStart(i)}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className=""
            >
              <p
                className={twMerge(
                  "font-manrope font-semibold text-[16px] mobile:text-[18px] tablet:text-[22px] xl:text-[30px] 3xl:text-[32px]",
                  isdark
                    ? "bg-[linear-gradient(94.67deg,_#FF1CF7_18.42%,_#EEEEFF_76.5%)] bg-clip-text text-transparent"
                    : "text-[#1A1A1A]",
                )}
              >
                {item.title}
              </p>
              <p className="max-w-[557px] 3xl:max-w-[750px] 4xl:max-w-[850px] font-medium font-manrope text-[12px] mobile:text-[14px] tablet:text-[16px] xl:text-[17px] 3xl:text-[18px]">
                {item.description}
              </p>
            </m.div>
          </m.div>
        ))}
      </div>

      {/* Crystal cube animation */}
      <div className="hidden sm:block absolute inset-0 pointer-events-none">
          {triggerFirstCubes && (
            <>
              {/* top left cube */}
              <m.div
                initial={{ y: -200, x: -500, rotate: 180, opacity: 0 }}
                animate={{ y: 0, x: 0, rotate: 0, opacity: 1 }}
                transition={{
                  type: "spring",
                  mass: 1,
                  stiffness: 45,
                  damping: 15,
                  bounce: 0,
                  restDelta: 0.0005,
                }}
                className="absolute top-[400px] left-[124px] xl:top-[496px] xl:left-[288px]"
              >
                <m.img
                  src="/assets/Cube.svg"
                  alt="top left cube"
                  className="w-[85px] h-[60px] xl:w-[171px] xl:h-[120px] rotate-90"
                  loading="lazy"
                  decoding="async"
                  style={{
                    transform: "translateZ(0)",
                    backfaceVisibility: "hidden",
                  }}
                  animate={{
                    rotate: [90, 100, 80, 90],
                    y: [0, -5, 0],
                    x: [0, 4, -4, 0],
                  }}
                  transition={{
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 10,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                />
              </m.div>

              {/* top right cube */}
              <m.div
                initial={{ y: -100, x: 200, rotate: 180, opacity: 0 }}
                animate={{ y: 0, x: 0, rotate: 0, opacity: 1 }}
                transition={{
                  type: "spring",
                  mass: 1,
                  stiffness: 45,
                  damping: 15,
                  bounce: 0,
                  restDelta: 0.0005,
                }}
                className="absolute top-[77px] -right-6 xl:-right-[60px]"
                style={{
                  willChange: "transform, opacity",
                  transform: "translateZ(0)",
                  backfaceVisibility: "hidden",
                }}
              >
                <m.img
                  src="/assets/Cube.svg"
                  alt="top right cube"
                  className="w-[60px] h-[42px] xl:w-[120px] xl:h-[84px] rotate-180"
                  loading="lazy"
                  decoding="async"
                  style={{
                    transform: "translateZ(0)",
                    backfaceVisibility: "hidden",
                  }}
                  animate={{
                    rotate: [180, 190, 170, 180],
                    y: [0, -5, 0],
                    x: [0, 4, -4, 0],
                  }}
                  transition={{
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 10,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                />
              </m.div>
            </>
          )}

          {triggerSecondCubes && (
            <>
              {/* bottom left cube */}
              <m.div
                initial={{ y: -100, x: -500, rotate: 180, opacity: 0 }}
                animate={{ y: 0, x: 0, rotate: 0, opacity: 1 }}
                transition={{
                  type: "spring",
                  mass: 1,
                  stiffness: 45,
                  damping: 15,
                  bounce: 0,
                  restDelta: 0.0005,
                }}
                className="absolute top-150 xl:top-[694px] -left-[40px]"
              >
                <m.img
                  src="/assets/Cube.svg"
                  alt="bottom left cube"
                  className="w-[247px] h-[173px] xl:w-[494px] xl:h-[346px] scale-110"
                  animate={{
                    rotate: [0, 20, 0],
                    y: [0, -5, 0],
                    x: [0, 4, -4, 0],
                  }}
                  transition={{
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 12,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                />
              </m.div>

              {/* bottom right cube */}
              <m.div
                initial={{ y: -100, x: 100, rotate: 180, opacity: 0 }}
                animate={{ y: 0, x: 0, rotate: 0, opacity: 1 }}
                transition={{
                  type: "spring",
                  mass: 1,
                  stiffness: 45,
                  damping: 15,
                  bounce: 0,
                  restDelta: 0.0005,
                }}
                className="absolute top-[575px] xl:top-[675px] -right-12 xl:-right-[150px]"
              >
                <m.img
                  src="/assets/Cube.svg"
                  alt="bottom right cube"
                  className="w-[98px] h-[68px] xl:w-[196px] xl:h-[137px] rotate-90"
                  animate={{
                    rotate: [90, 100, 80, 90],
                    y: [0, -5, 0],
                    x: [0, 3, -3, 0],
                  }}
                  transition={{
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 10,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                />
              </m.div>
            </>
          )}
      </div>
    </div>
  );
}
